import { Component, ViewChild, AfterViewInit, OnDestroy, Inject } from '@angular/core';
import { ChartOptions, ChartType, ChartDataSets } from 'chart.js';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MediaService } from 'app/services/media.service';
import { HttpService, ModalService } from 'app/services';
import { Label, BaseChartDirective } from 'ng2-charts';
import { debounceTime } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

interface IpdDataRecord {
  activations_min: number;
  activations_max?: number;
  period_start: string;
  unit_serial?: string;
  period_end: string;
  unit_id?: string;
  created?: string;
  id?: number;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
});

@Component({
  selector: 'app-insect-detection-data',
  templateUrl: './insect-detection-data.component.html',
  styleUrls: ['./insect-detection-data.component.scss']
})
export class InsectDetectionDataComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ipdChart') chart!: BaseChartDirective;

  private debounceSubject = new Subject<{ elementId: string; value: any }>();
  private theme: (string | null) = localStorage.getItem('skyhawk_ee__theme');
  private lastHoveredElement: (number | null) = null;
  private reversedRecords: IpdDataRecord[] = [];
  private debounceSubscription!: Subscription;
  private barColors: string[] = [];
  private refreshInterval: any;
  private deviceId: string;

  isDark: boolean = ((this.theme ?? 'theme-default') !== 'theme-default');
  records: IpdDataRecord[] = [];
  startDateTime: string;
  endDateTime: string;
  hideZeros = true;
  render = false;

  chartData: ChartDataSets[] = [];
  chartType: ChartType = 'bar';
  chartLabels: Label[] = [];
  chartLegend = true;

  chartOptions: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    hover: {
      mode: 'nearest',
      intersect: true
    },
    scales: {
      yAxes: [{
        ticks: {
          fontColor: (this.isDark ? '#c2c2c2' : '#374151'),
          beginAtZero: true
        }
      }],
      xAxes: [{
        ticks: {
          fontColor: (this.isDark ? '#c2c2c2' : '#374151'),
          maxTicksLimit: 24,
        }
      }]
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const index = context.dataIndex;
            const originalItem = this.reversedRecords[index];
            return `Range: ${this.formatRangeText(originalItem)}`;
          }
        }
      }
    },
    onHover: (event, activeElements: any) => {
      if (this.lastHoveredElement !== null) {
        document.querySelectorAll('tbody tr').forEach(row => {
          row.classList.remove('highlighted-row');
        });

        this.lastHoveredElement = null;
      }

      if (activeElements?.length > 0) {
        const clickedIndex = activeElements[0]._index;
        const _index = ((this.records.length - 1) - clickedIndex);

        this.lastHoveredElement = clickedIndex;
        this.scrollToTableRow(_index);
      }
    }
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private modalService: ModalService,
    public mediaService: MediaService,
    private httpService: HttpService
  ) {
    this.deviceId = data.deviceId;

    const end = new Date();
    const start = new Date(end);
    start.setHours(end.getHours() - 48);

    this.startDateTime = this.formatDateTimeLocal(start);
    this.endDateTime = this.formatDateTimeLocal(end);
  }

  private formatDateTimeLocal(date: Date) {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return (new Date(date.getTime() - tzOffset)).toISOString().slice(0, 16);
  }

  private renderChart() {
    this.chartLabels = this.reversedRecords?.map((item) => {
      const start = new Date(item.period_start);
      const end = new Date(item.period_end);

      const diff = (end.getTime() - start.getTime());
      const mid = new Date(start.getTime() + (diff / 2));

      return dateFormatter.format(mid).replace('at ', '~');
    });

    let newRecords = this.reversedRecords?.map((item) => {
      if (item.activations_min === 0) return 0;
      if (item.activations_min === 16385) return 16385;
      return Math.ceil((item.activations_min + (item.activations_max || 0)) / 2);
    });

    if (!newRecords?.length) newRecords = [0, 16385];
    this.barColors = this.reversedRecords?.map(() => '#42A5F5');

    this.chartData = [{
      hoverBackgroundColor: 'rgba(29, 78, 216, 0.9)',
      label: 'Activations (Midpoint Estimate)',
      borderColor: 'rgba(59, 130, 246, 1)',
      backgroundColor: this.barColors,
      data: newRecords
    }];
  }

  private scrollToTableRow(index: number) {
    this.highlightRow(index);

    const row = document.getElementById(`table-row-${index}`);
    const container = document.getElementById('tableWrapper');

    if (row && container) {
      const rowOffsetTop = row.offsetTop;
      const rowHeight = row.offsetHeight;
      const containerHeight = container.clientHeight;

      container.scrollTo({
        top: rowOffsetTop - (containerHeight / 2) + (rowHeight / 2),
        behavior: 'smooth'
      });
    }
  }

  private highlightRow(index: number) {
    document.querySelectorAll('tbody tr').forEach(row => {
      row.classList.remove('highlighted-row');
    });

    const activeRow = document.getElementById(`table-row-${index}`);
    if (activeRow) activeRow.classList.add('highlighted-row');
  }

  private debounceComplete(event: { elementId: string; value: any }) {
    if (event.elementId !== 'hideZeros') (this as any)[event.elementId] = event.value;
    this.getRecords();
  }

  private async getRecords(init?: boolean) {
    try {
      let start = new Date(this.startDateTime);
      let end = new Date(this.endDateTime);

      let filter = `filter[where][and][0][period_start][gte]=${encodeURIComponent(start.toISOString())}`;
      filter += `&filter[where][and][1][period_end][lte]=${encodeURIComponent(end.toISOString())}`;
      if (this.hideZeros) filter += `&filter[where][and][2][activations_min][neq]=0`;
      filter += '&filter[order]=period_start%20DESC';
      
      const req: IpdDataRecord[] = await this.httpService.getIpdData(this.deviceId, filter);
      if (JSON.stringify(req) === JSON.stringify(this.records)) {
        if (init) this.renderChart();
        return;
      }

      this.records = (req as IpdDataRecord[]);
      let recs = [...this.records];
      recs.reverse();

      this.reversedRecords = recs;
      this.renderChart();
    } catch (ex: any) {
      this.ngOnDestroy();
      this.modalService.errorDialog(ex);
    }
  }

  ngAfterViewInit(): void {
    this.getRecords(true);
    this.refreshInterval = setInterval(() => this.getRecords(), 10000);
    this.debounceSubscription = this.debounceSubject.pipe(debounceTime(700)).subscribe(this.debounceComplete.bind(this));

    setTimeout(() => {
      (document.activeElement as any)?.blur();
      this.render = true;
    }, 250);
  }

  ngOnDestroy(): void {
    if (this.debounceSubscription) this.debounceSubscription.unsubscribe();
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  formatRangeText(item: IpdDataRecord) {
    if (item.activations_min === 0) return '0';
    if (item.activations_min === 16385) return '16385+';
    return `${item.activations_min} - ${item.activations_max}`;
  }

  formatDate(date: string) {
    return dateFormatter.format(new Date(date))
  }

  rowEnter(event: any) {
    if (!this.chart || !this.chart.chart) return;

    const index = Number(event.target?.id.split('table-row-')[1]);
    const _index = ((this.records.length - 1) - index);

    let tmpColors = [...this.barColors];
    tmpColors[_index] = 'rgba(29, 78, 216, 0.9)';

    this.chartData[0].backgroundColor = tmpColors;
    this.chart.chart.update();
  }

  rowLeave() {
    if (!this.chart || !this.chart.chart) return;

    this.chartData[0].backgroundColor = this.barColors;
    this.chart.chart.update();
  }

  onInput(event: any, key: string) {
    let newValue;

    if (key === 'hideZeros') newValue = event.target.checked;
    else {
      const op = new Date(key === 'endDateTime' ? this.startDateTime : this.endDateTime);
      const elVal = event.target.value;

      const val = `${(this as any)[key]}`;
      const dateValue = new Date(elVal);

      if (key === 'endDateTime') {
        if (dateValue < op) newValue = this.formatDateTimeLocal(op);
      } else {
        if (dateValue > op) newValue = this.formatDateTimeLocal(op);
      }

      if (!newValue) newValue = elVal;
      else event.target.value = newValue;

      if (newValue === val) return;
    }

    this.debounceSubject.next({ elementId: key, value: newValue });
  }
}
